export const BrandMembershipRole = {
  ADMIN: "ADMIN",
  CREATOR: "CREATOR",
  REVIEWER: "REVIEWER",
  SCHEDULER: "SCHEDULER",
  VIEWER: "VIEWER",
} as const;
export type BrandMembershipRole =
  (typeof BrandMembershipRole)[keyof typeof BrandMembershipRole];

export const IdeaStatus = {
  BACKLOG: "BACKLOG",
  SELECTED: "SELECTED",
  CONVERTED: "CONVERTED",
} as const;
export type IdeaStatus = (typeof IdeaStatus)[keyof typeof IdeaStatus];

export const PipelineStage = {
  BRAINSTORM: "BRAINSTORM",
  BRIEFING: "BRIEFING",
  GENERATING: "GENERATING",
  REVIEW: "REVIEW",
  SCHEDULING: "SCHEDULING",
  PUBLISHED: "PUBLISHED",
  ANALYTICS: "ANALYTICS",
} as const;
export type PipelineStage =
  (typeof PipelineStage)[keyof typeof PipelineStage];

export const PieceStatus = {
  DRAFT: "DRAFT",
  PENDING_REVIEW: "PENDING_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  SCHEDULED: "SCHEDULED",
  PUBLISHED: "PUBLISHED",
  FAILED: "FAILED",
} as const;
export type PieceStatus = (typeof PieceStatus)[keyof typeof PieceStatus];

export const ScheduledPostStatus = {
  SCHEDULED: "SCHEDULED",
  PUBLISHED: "PUBLISHED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
} as const;
export type ScheduledPostStatus =
  (typeof ScheduledPostStatus)[keyof typeof ScheduledPostStatus];

export const SocialPlatform = {
  INSTAGRAM: "INSTAGRAM",
  TIKTOK: "TIKTOK",
  FACEBOOK: "FACEBOOK",
  YOUTUBE: "YOUTUBE",
  LINKEDIN: "LINKEDIN",
  TWITTER: "TWITTER",
  THREADS: "THREADS",
  PINTEREST: "PINTEREST",
  REDDIT: "REDDIT",
  GOOGLEBUSINESS: "GOOGLEBUSINESS",
} as const;
export type SocialPlatform =
  (typeof SocialPlatform)[keyof typeof SocialPlatform];

/**
 * Platforms whose DMs/comments flow into the unified social inbox. Limited to
 * what Zernio's inbox API supports AND we connect — TikTok / YouTube / LinkedIn
 * have no DM/comment inbox API, so they're intentionally excluded. Used to scope
 * the inbox gate, list/unread queries, and the worker sync.
 */
export const INBOX_PLATFORMS: SocialPlatform[] = [
  SocialPlatform.INSTAGRAM,
  SocialPlatform.FACEBOOK,
  SocialPlatform.TWITTER,
];

export const SocialAccountStatus = {
  ACTIVE: "ACTIVE",
  EXPIRED: "EXPIRED",
  DISCONNECTED: "DISCONNECTED",
  ERROR: "ERROR",
} as const;
export type SocialAccountStatus =
  (typeof SocialAccountStatus)[keyof typeof SocialAccountStatus];

export const AssetType = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  LOGO: "LOGO",
  FONT: "FONT",
  DOCUMENT: "DOCUMENT",
} as const;
export type AssetType = (typeof AssetType)[keyof typeof AssetType];

export const BrandMemorySource = {
  POST_PERFORMANCE: "POST_PERFORMANCE",
  BRAND_VOICE: "BRAND_VOICE",
  AUDIENCE_INSIGHT: "AUDIENCE_INSIGHT",
  CONTENT_EXAMPLE: "CONTENT_EXAMPLE",
} as const;
export type BrandMemorySource =
  (typeof BrandMemorySource)[keyof typeof BrandMemorySource];

export const AgentJobStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  WAITING_APPROVAL: "WAITING_APPROVAL",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export type AgentJobStatus =
  (typeof AgentJobStatus)[keyof typeof AgentJobStatus];

export const NotificationChannel = {
  EMAIL: "EMAIL",
  TELEGRAM: "TELEGRAM",
  SLACK: "SLACK",
} as const;
export type NotificationChannel =
  (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const ContentFormat = {
  REEL: "REEL",
  CAROUSEL: "CAROUSEL",
  CAROUSEL_SLIDE: "CAROUSEL_SLIDE",
  POST: "POST",
  STORY: "STORY",
  SHORT: "SHORT",
} as const;
export type ContentFormat =
  (typeof ContentFormat)[keyof typeof ContentFormat];

export const ChatMessageRole = {
  USER: "USER",
  ASSISTANT: "ASSISTANT",
  TOOL: "TOOL",
} as const;
export type ChatMessageRole =
  (typeof ChatMessageRole)[keyof typeof ChatMessageRole];

export const FontSource = {
  GOOGLE: "GOOGLE",
  CUSTOM: "CUSTOM",
} as const;
export type FontSource = (typeof FontSource)[keyof typeof FontSource];

export const PostType = {
  IG_FEED: "IG_FEED",
  FB_FEED: "FB_FEED",
  LINKEDIN_TEXT_OR_IMAGE: "LINKEDIN_TEXT_OR_IMAGE",
  X_TWEET: "X_TWEET",
  THREADS_POST: "THREADS_POST",
  REDDIT_LINK: "REDDIT_LINK",
  REDDIT_MEDIA: "REDDIT_MEDIA",
  GBP_UPDATE: "GBP_UPDATE",
  IG_CAROUSEL: "IG_CAROUSEL",
  LINKEDIN_DOC: "LINKEDIN_DOC",
  X_THREAD: "X_THREAD",
  THREADS_THREAD: "THREADS_THREAD",
  REDDIT_SELF: "REDDIT_SELF",
  IG_REEL: "IG_REEL",
  IG_STORY: "IG_STORY",
  FB_REEL: "FB_REEL",
  FB_STORY: "FB_STORY",
  TIKTOK_VIDEO: "TIKTOK_VIDEO",
  TIKTOK_PHOTO: "TIKTOK_PHOTO",
  YOUTUBE_SHORT: "YOUTUBE_SHORT",
  YOUTUBE_LONG: "YOUTUBE_LONG",
} as const;
export type PostType = (typeof PostType)[keyof typeof PostType];

/**
 * Prompt-registry keys for prompts that aren't tied to a PostType (media
 * generation, assistants, …). The registry stores them in the same `postType`
 * field as PostTypes — see PromptKey.
 */
export const NamedPromptKey = {
  /** Library → Generate video: "Write the shot for me" (system prompt). */
  VIDEO_BRIEF: "VIDEO_BRIEF",
} as const;
export type NamedPromptKey = (typeof NamedPromptKey)[keyof typeof NamedPromptKey];

/** What the prompt registry is keyed by: a PostType or a NamedPromptKey. */
export type PromptKey = PostType | NamedPromptKey;
export const PROMPT_KEYS: readonly PromptKey[] = [
  ...Object.values(PostType),
  ...Object.values(NamedPromptKey),
];

export const AssetRole = {
  LOGO_PRIMARY: "LOGO_PRIMARY",
  FONT_FILE: "FONT_FILE",
} as const;
export type AssetRole = (typeof AssetRole)[keyof typeof AssetRole];

export const InboxStatus = {
  OPEN: "OPEN",
  SNOOZED: "SNOOZED",
  DONE: "DONE",
  // Hidden/dismissed (e.g. spam). Removed from the default OPEN inbox view and
  // excluded from unread counts; reversible. Local-only — does not delete on
  // the platform (inbound DMs can't be deleted there anyway).
  ARCHIVED: "ARCHIVED",
} as const;
export type InboxStatus = (typeof InboxStatus)[keyof typeof InboxStatus];

export const InboxItemKind = {
  DM: "DM",
  COMMENT: "COMMENT",
} as const;
export type InboxItemKind = (typeof InboxItemKind)[keyof typeof InboxItemKind];

export const InboxDeliveryStatus = {
  PENDING: "PENDING",
  SENT: "SENT",
  FAILED: "FAILED",
} as const;
export type InboxDeliveryStatus =
  (typeof InboxDeliveryStatus)[keyof typeof InboxDeliveryStatus];

export const AdBoostStatus = {
  SUGGESTED: "SUGGESTED",
  CREATING: "CREATING",
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  COMPLETED: "COMPLETED",
  REJECTED: "REJECTED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
} as const;
export type AdBoostStatus =
  (typeof AdBoostStatus)[keyof typeof AdBoostStatus];
